import React from "react";
import "./GoogleLogin.css";

interface GoogleLoginModalProps {
  isOpen: boolean;
  onClose: () => void;
  // Pre-built login URL to navigate to. The parent owns construction so it can
  // include the `next=` query param pointing at the current path.
  loginUrl: string;
  onLoginClick?: () => void;
  disabled?: boolean;
  title?: string;
  description?: string;
}

const GoogleLoginModal: React.FC<GoogleLoginModalProps> = ({
  isOpen,
  onClose,
  loginUrl,
  onLoginClick,
  disabled = false,
  title = "Sign in to continue",
  description = "Please sign in with your Google account to access this feature.",
}) => {
  const handleLoginClick = () => {
    if (onLoginClick) onLoginClick();
    // Top-level navigation to the playground backend's OAuth entry point.
    // The backend handles the entire OAuth dance and sets an httpOnly cookie
    // before redirecting us back; the browser never sees the JWT.
    window.location.href = loginUrl;
  };

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleKeyDown = (e: KeyboardEvent) => {
    if (e.key === "Escape") {
      onClose();
    }
  };

  React.useEffect(() => {
    if (isOpen) {
      document.addEventListener("keydown", handleKeyDown);
      document.body.style.overflow = "hidden";
    }

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = "unset";
    };
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div className="google-login-modal-overlay" onClick={handleBackdropClick}>
      <div className="google-login-modal">
        <div className="google-login-modal-header">
          <h2 className="google-login-modal-title">{title}</h2>
        </div>

        <div className="google-login-modal-body">
          <p className="google-login-modal-description">{description}</p>

          <button
            className={`google-login-btn ${disabled ? "disabled" : ""}`}
            onClick={handleLoginClick}
            disabled={disabled}
          >
            <div className="google-login-content">
              <svg
                className="google-icon"
                viewBox="0 0 24 24"
                width="20"
                height="20"
              >
                <path
                  fill="#4285F4"
                  d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                />
                <path
                  fill="#34A853"
                  d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                />
                <path
                  fill="#FBBC05"
                  d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                />
                <path
                  fill="#EA4335"
                  d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                />
              </svg>
              <span className="google-login-text">Sign in with Google</span>
            </div>
          </button>
        </div>
      </div>
    </div>
  );
};

export default GoogleLoginModal;
